import type { Address, LoyaltyAccount, LoyaltyTransaction, Order, User, WalletTransaction } from '@/types';
import { demoAddresses, loyaltySeed, ordersSeed, walletSeed } from '@/data/account';
import { userForEmail } from '@/data/users';

/** Simulated network latency so loading states are real. Set VITE_MOCK_LATENCY=0 to disable. */
const base = Number(import.meta.env.VITE_MOCK_LATENCY ?? 350);
export const delay = <T>(value: T, ms = base): Promise<T> =>
  new Promise((res) => setTimeout(() => res(structuredClone(value)), ms + Math.random() * ms * 0.5));

/** Query `?fail=1` forces service errors, to exercise error states during manual testing. */
export const shouldFail = () =>
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('fail');

const SESSION_KEY = 'mce.mock.session';
const DB_KEY = 'mce.mock.db.v1';

interface Persisted {
  addresses: Address[];
  orders: Order[];
  wallet: { balance: number; tx: WalletTransaction[] };
  loyalty: { account: LoyaltyAccount; transactions: LoyaltyTransaction[] };
}

const seed = (): Persisted => ({
  addresses: structuredClone(demoAddresses),
  orders: structuredClone(ordersSeed),
  wallet: { balance: walletSeed[0].balanceAfter, tx: structuredClone(walletSeed) },
  loyalty: structuredClone(loyaltySeed),
});

function load(): Persisted {
  try {
    const raw = localStorage.getItem(DB_KEY);
    if (raw) return JSON.parse(raw) as Persisted;
  } catch { /* storage unavailable or corrupt: fall back to seed */ }
  return seed();
}

/**
 * MOCK database. Held in memory and mirrored to localStorage so demo flows (place an order, top up the wallet)
 * survive a page refresh. This is browser-local demo data, NOT server persistence. `db.reset()` restores the seed.
 */
export const db = {
  user: null as User | null,
  ...load(),
  nextId: 1,
  save() {
    try {
      const { addresses, orders, wallet, loyalty } = this;
      localStorage.setItem(DB_KEY, JSON.stringify({ addresses, orders, wallet, loyalty }));
    } catch { /* storage unavailable: demo still works for this page view */ }
  },
  reset() {
    Object.assign(this, seed());
    this.save();
  },
};

/** Session holds the signed-in demo email only; the user (and role) is derived from it in `data/users.ts`. */
export const session = {
  read: (): string | null => {
    try { return sessionStorage.getItem(SESSION_KEY); } catch { return null; }
  },
  write: (email: string | null) => {
    try { email ? sessionStorage.setItem(SESSION_KEY, email) : sessionStorage.removeItem(SESSION_KEY); } catch { /* storage unavailable */ }
  },
};

const restored = session.read();
if (restored) db.user = userForEmail(restored);

export const uid = (p: string) => `${p}_${Date.now().toString(36)}${db.nextId++}`;
